import { Button, Grid, GridItem, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MinimalOrgDto, SupplierRelationsDto } from "../../../autogen/bff-api";
import { useOrganizationProfileUrls } from "../useOrganizationProfileUrls";
import { SearchResult } from "./SearchResult";
import styles from "./SearchResults.module.css";

interface Props {
  organizations?: MinimalOrgDto[];
  relations?: SupplierRelationsDto[];
  isSearching: boolean;
  count?: number;
  onResetFilters?: () => void;
  onClearSearch?: () => void;
  emptyMessageShownAbove?: boolean;
}

export const SearchResults = ({
  organizations,
  relations,
  isSearching,
  count,
  onResetFilters,
  onClearSearch,
  emptyMessageShownAbove = false,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profileUrls = useOrganizationProfileUrls();
  const relationsOf = new Map((relations ?? []).map((r) => [r.organizationId, r.relations]));

  return (
    <Grid gridTemplateColumns={"1fr"} gridTemplateRows="minmax(50px, 1fr)" gridAutoRows={"minmax(100px, auto)"}>
      <GridItem display="flex" alignItems={"center"}>
        <Text fontSize="md" marginLeft={4}>
          {organizations && t("{{count}} companies found", { count: count ?? organizations.length })}
        </Text>
        {isSearching && <Spinner ml="8px" size={"xs"} />}
      </GridItem>

      {organizations?.length === 0 && (onResetFilters || onClearSearch) && (
        <GridItem>
          <Stack spacing="2" align="start" ml="4">
            {!emptyMessageShownAbove && (
              <Text fontSize="sm" color="smMuted">
                {t("No companies match the search and filters.")}
              </Text>
            )}
            <HStack spacing="2">
              {onClearSearch && (
                <Button size="sm" variant="outline" onClick={onClearSearch}>
                  {t("Clear the search")}
                </Button>
              )}
              {onResetFilters && (
                <Button size="sm" variant="outline" onClick={onResetFilters}>
                  {t("Reset filters")}
                </Button>
              )}
            </HStack>
          </Stack>
        </GridItem>
      )}
      {organizations ? (
        organizations.map((o) => (
          <GridItem
            key={o.id}
            role="group"
            aria-label={o.name}
            className={styles.result}
            py="5"
            onClick={() => {
              if (o.type === "OrganizationEntry") {
                navigate(profileUrls.goToEntry(o.id, profileUrls.sections.information));
              } else {
                navigate(profileUrls.go(o.id, profileUrls.sections.information));
              }
            }}
          >
            <SearchResult organization={o} relations={relationsOf.get(o.id) ?? []} />
          </GridItem>
        ))
      ) : (
        <div>{t("Loading")}...</div>
      )}
    </Grid>
  );
};
